import { useState, type ReactNode } from "react";

type Project = {
  title: string;
  titleZh?: string;
  blurb?: string;
  image?: string;
  city?: string;
  category?: string;
  kind?: string;
  /** Experience catalog uses the experience card (city + category). Listings use the place card. */
  layout?: "experience" | "place";
  price?: string | number | null;
  status?: string;
  bookUrl?: string;
};

type City = {
  name: string;
  nameZh?: string;
  blurb?: string;
  blurbZh?: string;
  image?: string;
  heat?: string;
  status?: string;
};

type Article = {
  title: string;
  excerpt?: string;
  body?: string;
  kind?: string;
  city?: string;
  image?: string;
  tags?: string[];
};

const CATEGORY: Record<string, { label: string; tone: string }> = {
  beauty: { label: "Hair & Beauty", tone: "pink" },
  wellness: { label: "TCM & Wellness", tone: "emerald" },
  culture: { label: "Hanfu & Culture", tone: "amber" },
  food: { label: "Food & Night Markets", tone: "orange" },
  nature: { label: "Pandas & Wildlife", tone: "lime" },
  sightseeing: { label: "City & Nightlife", tone: "sky" },
  outdoor: { label: "Outdoor & Nature", tone: "lime" },
  shopping: { label: "Shopping & Markets", tone: "violet" },
  medical: { label: "Medical & Dental", tone: "sky" },
  entertainment: { label: "Entertainment", tone: "fuchsia" },
};

const KIND: Record<string, { label: string; tone: string }> = {
  experience: { label: "Experiences", tone: "fuchsia" },
  food: { label: "Food", tone: "orange" },
  sight: { label: "Sights", tone: "violet" },
};

function stay(event: { preventDefault: () => void }) {
  event.preventDefault();
}

function labelize(value?: string) {
  if (!value) return "";
  return value
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function priceLabel(price?: string | number | null) {
  const amount = typeof price === "number" ? price : Number(price);
  if (!Number.isFinite(amount) || amount <= 0) return "";
  return `From $${Math.round(amount / 7.2).toLocaleString("en-US")}`;
}

function pill(tone: string, text: string) {
  return (
    <span className={`exp-card__cat-pill exp-card__cat-pill--${tone}`} key={`${tone}-${text}`}>
      {text}
    </span>
  );
}

function categoryMeta(category?: string) {
  if (!category) return null;
  return CATEGORY[category] ?? { label: labelize(category), tone: "violet" };
}

function Photo({ src, alt }: { src?: string; alt: string }) {
  if (!src) return null;
  return <img alt={alt} src={src} />;
}

const iconSvg = {
  "aria-hidden": true as const,
  fill: "none",
  height: 18,
  stroke: "currentColor",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  strokeWidth: 1.8,
  viewBox: "0 0 24 24",
};

function PriceIcon() {
  return (
    <svg {...iconSvg}>
      <rect height="12" rx="2" width="18" x="3" y="6" />
      <path d="M3 10h18" />
      <path d="M8 15h.01" />
    </svg>
  );
}

function PreviewFrame({ card, detail }: { card: ReactNode; detail: ReactNode }) {
  const [tab, setTab] = useState<"card" | "detail">("card");
  return (
    <aside className="ops-preview">
      <div className="ops-tabs" role="tablist">
        <button className={tab === "card" ? "ops-tab ops-tab--active" : "ops-tab"} onClick={() => setTab("card")} type="button">
          Card
        </button>
        <button className={tab === "detail" ? "ops-tab ops-tab--active" : "ops-tab"} onClick={() => setTab("detail")} type="button">
          Detail
        </button>
      </div>
      <div className={tab === "card" ? "ops-preview__pane ops-preview__pane--card ops-stage" : "ops-preview__pane ops-stage"}>
        {tab === "card" ? card : detail}
      </div>
    </aside>
  );
}

function ExperienceCard({ item }: { item: Project }) {
  const city = labelize(item.city);
  const kind = KIND[item.kind || "experience"] ?? KIND.experience;
  const category = categoryMeta(item.category);
  const price = priceLabel(item.price);
  const experience = item.layout === "experience";
  const title = item.title || "Experience title";
  return (
    <article className="exp-card exp-card--action">
      <div className="exp-card__media">
        <a className="exp-card__media-link" href="#preview" onClick={stay} tabIndex={-1}>
          <Photo alt={title} src={item.image} />
        </a>
      </div>
      <a className="exp-card__body-link" href="#preview" onClick={stay}>
        <h3 className="exp-card__title">{title}</h3>
        <span className="exp-card__tag-row">
          {experience ? (
            <>
              {city ? pill("sky", city) : null}
              {category ? pill(category.tone, category.label) : null}
            </>
          ) : (
            <>
              {pill(kind.tone, kind.label)}
              {category ? pill(category.tone, category.label) : null}
            </>
          )}
        </span>
        {item.blurb ? <p className="exp-card__blurb">{item.blurb}</p> : null}
        {price ? (
          <span className="exp-card__meta-row">
            <span className="exp-card__price" title={price}>
              {price}
            </span>
          </span>
        ) : null}
      </a>
      <div className="exp-card__actions">
        <button className="btn btn--secondary trip-add-btn trip-add-btn--compact" type="button">
          Add to trip
        </button>
        {item.bookUrl ? (
          <a className="btn btn--primary trip-add-btn trip-add-btn--compact" href={item.bookUrl} rel="nofollow sponsored noopener noreferrer" target="_blank">
            Book
          </a>
        ) : (
          <button className="btn btn--primary trip-add-btn trip-add-btn--compact" type="button">
            Ask advisor
          </button>
        )}
      </div>
    </article>
  );
}

function Fact({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="place-guide__fact">
      <span className="place-guide__fact-icon">{icon}</span>
      <div className="place-guide__fact-copy">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function ExperienceDetail({ item }: { item: Project }) {
  const city = labelize(item.city);
  const kind = KIND[item.kind || "experience"] ?? KIND.experience;
  const category = categoryMeta(item.category);
  const price = priceLabel(item.price);
  const title = item.titleZh || item.title || "Experience title";
  const mapQuery = encodeURIComponent(`${item.title || title}${city ? `, ${city}` : ""}, China`);
  return (
    <div className="place-detail">
      <section className="place-detail__hero">
        <div className="place-detail__media">
          <Photo alt={title} src={item.image} />
        </div>
        <div className="place-detail__summary">
          <nav aria-label="taxonomy" className="place-detail__crumbs">
            {city ? (
              <a href="#preview" onClick={stay}>
                {city}
              </a>
            ) : (
              <span>China</span>
            )}
            <span aria-hidden>/</span>
            <span>{kind.label}</span>
            {category ? (
              <>
                <span aria-hidden>/</span>
                <span>{category.label}</span>
              </>
            ) : null}
          </nav>
          <h1 className="place-detail__title">{title}</h1>
          {item.blurb ? <p className="place-detail__blurb">{item.blurb}</p> : null}
          <div className="place-detail__tag-row">
            {pill(kind.tone, kind.label)}
            {category ? pill(category.tone, category.label) : null}
          </div>
          {price ? (
            <p className="place-detail__price">
              <strong>{price}</strong>
            </p>
          ) : null}
          {city ? (
            <p className="place-detail__guide-link">
              <a href="#preview" onClick={stay}>
                {city} city guide →
              </a>
            </p>
          ) : null}
        </div>
      </section>

      <div className="place-detail__body">
        <div className="place-detail__main">
          <article className="place-block place-guide" id="overview">
            {item.blurb ? (
              <section className="place-guide__section" id="overview-copy">
                <h3>Overview</h3>
                <div className="place-guide__overview">
                  <article className="place-guide__block place-guide__block--lead">
                    <h4>{item.title || title}</h4>
                    <p>{item.blurb}</p>
                  </article>
                </div>
              </section>
            ) : null}
            {price ? (
              <section className="place-guide__section" id="practical">
                <h3>Practical</h3>
                <div className="place-guide__facts">
                  <Fact icon={<PriceIcon />} label="Price" value={price} />
                </div>
              </section>
            ) : null}
            <section className="place-guide__section" id="location">
              <h3>Location & how to go</h3>
              <p className="place-guide__addr">{city ? `${city} · exact pin confirmed with partner` : "Exact pin confirmed with partner"}</p>
              <div className="place-guide__map">
                <iframe
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${mapQuery}&hl=en&z=13&output=embed`}
                  title="Map"
                />
              </div>
            </section>
          </article>
        </div>
        <aside className="place-detail__side">
          <div className="place-side-stack place-side-card--sticky">
            <div className="place-side-card place-side-card--convert">
              <p className="place-side-card__eyebrow">
                {kind.label}
                {city ? ` · ${city}` : ""}
              </p>
              <ul className="place-side-card__bullets">
                <li>English-friendly hosts available</li>
                <li>Advisor helps book & pay clarity</li>
                <li>Add to trip for a planner report</li>
              </ul>
              <div className="place-side-card__cta">
                {item.bookUrl ? (
                  <a className="btn btn--primary" href={item.bookUrl} rel="nofollow sponsored noopener noreferrer" target="_blank">
                    Book now
                  </a>
                ) : (
                  <button className="btn btn--primary" type="button">
                    Ask advisor
                  </button>
                )}
                <button className="btn btn--secondary trip-add-btn" type="button">
                  Add to trip
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export function ExperiencePreview({ item }: { item: Project }) {
  return <PreviewFrame card={<ExperienceCard item={item} />} detail={<ExperienceDetail item={item} />} />;
}

function cityHeat(heat?: string) {
  if (heat === "P0") return "Popular";
  if (heat === "P2") return "Explore";
  return "Featured";
}

export function CityPreview({ item }: { item: City }) {
  const name = item.name || "City name";
  const zh = item.nameZh || "";
  const heat = cityHeat(item.heat);
  const lede = item.blurb || "";
  const mapQuery = encodeURIComponent(`${name}, China`);
  return (
    <PreviewFrame
      card={
        <a className="city-card" href="#preview" onClick={stay} style={item.image ? undefined : { background: "linear-gradient(160deg, #6b4ef6, #18224b)" }}>
          <Photo alt="" src={item.image} />
          <span className="city-card__veil" />
          <span className="city-card__body">
            <h3 className="city-card__name">{name}</h3>
            {lede ? <p className="city-card__blurb">{lede}</p> : null}
            <span className="exp-card__cta" style={{ marginTop: "0.5rem", display: "inline-block" }}>
              City guide →
            </span>
          </span>
        </a>
      }
      detail={
        <div className="city-guide">
          <header className="city-guide__hero">
            <div className="city-guide__cover">
              <Photo alt={name} src={item.image} />
              <span className="city-guide__heat">{heat}</span>
            </div>
            <div className="city-guide__head">
              <div className="city-guide__intro">
                <p className="city-guide__eyebrow">{[name, zh].filter(Boolean).join(" · ")}</p>
                <h1 className="city-guide__title">{name}</h1>
                {lede ? <p className="city-guide__lede">{lede}</p> : null}
              </div>
            </div>
          </header>
          <div className="place-detail__body city-guide__layout">
            <div className="place-detail__main">
              <article className="place-block place-guide" id="overview">
                {lede ? (
                  <section className="place-guide__section" id="overview-copy">
                    <h3>Overview</h3>
                    <div className="place-guide__overview">
                      <article className="place-guide__block place-guide__block--lead">
                        <h4>{name}</h4>
                        <p>{lede}</p>
                      </article>
                    </div>
                  </section>
                ) : null}
                <section className="place-guide__section" id="location">
                  <h3>Location & how to go</h3>
                  <p className="place-guide__addr">{name}, China</p>
                  <div className="place-guide__map place-guide__map--city">
                    <iframe
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://maps.google.com/maps?q=${mapQuery}&hl=en&z=11&output=embed`}
                      title="Map"
                    />
                  </div>
                </section>
              </article>
            </div>
            <aside className="place-detail__side">
              <div className="place-side-stack place-side-card--sticky">
                <div className="place-side-card place-side-card--convert">
                  <p className="place-side-card__eyebrow">City guide · {name}</p>
                  <ul className="place-side-card__bullets">
                    <li>Advisor helps sequence days & must-sees</li>
                    <li>Add sights, food & experiences to your trip</li>
                    <li>Generate a planner report before booking</li>
                  </ul>
                  <div className="place-side-card__cta">
                    <button className="btn btn--primary" type="button">
                      Ask advisor
                    </button>
                    <button className="btn btn--secondary" type="button">
                      Plan my trip
                    </button>
                  </div>
                  <p className="place-side-card__hint">Ask advisor to lock a rhythm, or open the planner and add stops yourself.</p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      }
    />
  );
}

function articleMeta(kind?: string) {
  const key = (kind || "").toLowerCase();
  if (key === "community_question" || key === "qa") return { qa: true, label: "Q&A", tone: "violet", hub: "Community" };
  if (key === "news") return { qa: false, label: "News", tone: "sky", hub: "Insights" };
  if (key === "policy") return { qa: false, label: "Policy", tone: "amber", hub: "Insights" };
  if (key === "trending") return { qa: false, label: "Trending", tone: "pink", hub: "Insights" };
  return { qa: false, label: "Guides", tone: "emerald", hub: "Insights" };
}

function inlineNodes(text: string, keyBase: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.filter(Boolean).map((part, index) => {
    const key = `${keyBase}-${index}`;
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong className="cp-md__strong" key={key}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link) {
      return (
        <a className="cp-md__link cp-md__link--external" href={link[2]} key={key} rel="noopener noreferrer" target="_blank">
          {link[1]}
        </a>
      );
    }
    return <span key={key}>{part}</span>;
  });
}

function ArticleBody({ source }: { source: string }) {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  const toc: Array<{ id: string; label: string }> = [];
  let paragraph: string[] = [];
  let list: string[] = [];
  let listKind: "ul" | "ol" | null = null;
  let n = 0;

  function flushParagraph() {
    const text = paragraph.join(" ").trim();
    paragraph = [];
    if (!text) return;
    blocks.push(
      <p className="cp-md__p" key={`p-${blocks.length}`}>
        {inlineNodes(text, `p-${blocks.length}`)}
      </p>,
    );
  }

  function flushList() {
    if (!listKind || list.length === 0) {
      list = [];
      listKind = null;
      return;
    }
    const Tag = listKind;
    const items = list;
    const kind = listKind;
    list = [];
    listKind = null;
    blocks.push(
      <Tag className={kind === "ol" ? "cp-md__ol" : "cp-md__ul"} key={`l-${blocks.length}`}>
        {items.map((item, index) => (
          <li className="cp-md__li" key={`${kind}-${index}`}>
            {inlineNodes(item, `${kind}-${index}`)}
          </li>
        ))}
      </Tag>,
    );
  }

  for (const raw of lines) {
    const line = raw.trim();
    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    const bullet = /^[-*]\s+(.+)$/.exec(line);
    const numbered = /^\d+\.\s+(.+)$/.exec(line);
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }
    if (heading) {
      flushParagraph();
      flushList();
      const level = heading[1].length;
      const label = heading[2].replace(/\*\*/g, "");
      const id = `section-${n++}`;
      if (level <= 2) toc.push({ id, label });
      const Tag = level === 1 ? "h2" : level === 2 ? "h2" : "h3";
      blocks.push(
        <Tag className={Tag === "h3" ? "cp-md__h3" : "cp-md__h2"} id={id} key={id}>
          {inlineNodes(heading[2], id)}
        </Tag>,
      );
      continue;
    }
    if (bullet || numbered) {
      flushParagraph();
      const nextKind = bullet ? "ul" : "ol";
      if (listKind && listKind !== nextKind) flushList();
      listKind = nextKind;
      list.push((bullet || numbered)![1]);
      continue;
    }
    flushList();
    paragraph.push(line.replace(/^>\s?/, ""));
  }
  flushParagraph();
  flushList();

  return {
    toc,
    node: <div className="cp-md cp-md--article cp-article-md">{blocks}</div>,
  };
}

export function ArticlePreview({ item }: { item: Article }) {
  const meta = articleMeta(item.kind);
  const title = item.title || (meta.qa ? "Question" : "Article title");
  const tags = (item.tags || []).filter((tag) => tag && tag !== item.city);
  const city = labelize(item.city);
  const story = ArticleBody({ source: item.body || "" });
  const excerpt = item.excerpt || "";
  return (
    <PreviewFrame
      card={
        meta.qa ? (
          <a className="community-related-card" href="#preview" onClick={stay}>
            {item.image ? (
              <div aria-hidden className="community-related-card__media" role="img" style={{ backgroundImage: `url(${item.image})` }} />
            ) : (
              <div className="community-related-card__media community-related-card__media--topic">
                <h3 className="community-related-card__topic-title">{title}</h3>
              </div>
            )}
            <div className="community-related-card__body">
              <p className="community-related-card__meta">
                <span className="community-related-card__cat">{meta.label}</span>
                {city ? <span>{city}</span> : null}
              </p>
              {item.image ? <h3 className="community-related-card__title">{title}</h3> : null}
              {excerpt ? <p className="community-related-card__excerpt">{excerpt}</p> : null}
            </div>
          </a>
        ) : (
          <a className="insight-card" href="#preview" onClick={stay}>
            <div aria-hidden className="insight-card__media" role="img" style={item.image ? { backgroundImage: `url(${item.image})` } : undefined} />
            <div className="insight-card__body">
              <p className="insight-card__meta">
                <span>{meta.label}</span>
                {city ? <span aria-hidden>·</span> : null}
                {city ? <span>{city}</span> : null}
              </p>
              <h3 className="insight-card__title">{title}</h3>
              {excerpt ? <p className="insight-card__excerpt">{excerpt}</p> : null}
            </div>
          </a>
        )
      }
      detail={
        <article className="cp-article">
          <div className="cp-article__layout cp-article__layout--rails">
            <aside aria-label="On this page" className="cp-article__toc-rail">
              <div className="place-side-stack place-side-card--sticky">
                <nav aria-label="On this page" className="place-side-card">
                  <p className="place-side-card__title">On this page</p>
                  {story.toc.length > 0 ? (
                    <ol className="cp-article__toc-list">
                      {story.toc.map((entry) => (
                        <li key={entry.id}>
                          <a href={`#${entry.id}`}>{entry.label}</a>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="place-side-card__hint">Short article — scroll the body.</p>
                  )}
                </nav>
              </div>
            </aside>
            <div className="cp-article__main">
              <nav aria-label="Breadcrumb" className="cp-article__crumbs">
                <a href="#preview" onClick={stay}>
                  {meta.hub}
                </a>
                <span aria-hidden> / </span>
                <a href="#preview" onClick={stay}>
                  {meta.label}
                </a>
              </nav>
              <div className="cp-article__cat-row">{pill(meta.tone, meta.label)}</div>
              <h1 className="cp-article__title">{title}</h1>
              {excerpt ? <p className="cp-article__dek">{excerpt}</p> : null}
              {tags.length > 0 ? (
                <div aria-label="Tags" className="cp-article__tags">
                  {tags.map((tag) => pill("violet", labelize(tag)))}
                </div>
              ) : null}
              {city ? (
                <p className="cp-article__byline">
                  <span>{city}</span>
                </p>
              ) : null}
              {item.image ? (
                <div className="cp-article__cover">
                  <img alt="" src={item.image} />
                </div>
              ) : null}
              {item.body ? (
                <section aria-label="Story" className="cp-article__story">
                  <p className="cp-article__story-label">{meta.qa ? "Answer" : "Story"}</p>
                  {story.node}
                </section>
              ) : null}
            </div>
            <aside aria-label="Browse by category" className="cp-article__aside cp-article__aside--rail place-detail__side">
              <div className="place-side-stack place-side-card--sticky">
                <div className="place-side-card">
                  <p className="place-side-card__title">Keep browsing</p>
                  <p className="place-detail__guide-link">
                    <a href="#preview" onClick={stay}>
                      {meta.qa ? "All Q&A" : "All Insights"}
                    </a>
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </article>
      }
    />
  );
}
